import { useState } from 'react';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@baseComponents'
import CredentialItem from './CredentialItem';
import { CredentialInput, OfficialCredential } from '../../../networkRequest/officialsService';
import AddCredentialDialog from './AddCredentialDialog';
import { Toast } from '@components';
import ProtectedElement from '../../../auth/ProtectedElement';
import { credentialManagementRoles } from '../../../utils/authorization';
import { useUser } from '../../../hooks/useUser';

type CredentialsListProps = {
  officialId: number;
  credentials: OfficialCredential[];
  refreshData: () => Promise<void>;
};

const CredentialsList = ({ officialId, credentials, refreshData }: CredentialsListProps) => {
  const theme = useTheme();
  const { user } = useUser();
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const onDialogSave = async (_c: CredentialInput) => {
    setSuccessMessage('Credential saved');
    setIsDialogOpen(false);
    refreshData();
  };

  const onError = (error: string) => {
    setErrorMessage(error);
    setIsDialogOpen(false);
  };

  const clearError = () => setErrorMessage('');
  const clearSuccess = () => setSuccessMessage('');
  return (
    <Box>
      <Grid
        container
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-end'
        }}>
        <Grid
          container
          sx={{
            alignItems: 'flex-end',
            gap: 2
          }}>
          <Typography variant='l' component='h2'>Issued Credentials</Typography>
          <Typography variant='bodyMedium' sx={{ color: theme.palette.text.secondary }}>
            {credentials?.length ? `${credentials.length} Results` : null}
          </Typography>
        </Grid>
        <ProtectedElement userProfile={user!.profile} allowedRoles={credentialManagementRoles}>
          <Button
            mode='text'
            text='Credential'
            startIcon={<AddIcon fontSize='small' sx={{ marginRight: 1 }} />}
            onClick={() => setIsDialogOpen(true)}
          />
        </ProtectedElement>
      </Grid>
      <Typography variant='bodyMedium' component='p' sx={{
        mt: 0.5
      }}>
        Manage the secure identifiers that link to the user&apos;s Official Capacity ID (OCID).
      </Typography>
      <Grid container spacing={2} sx={{
        mt: 2
      }}>
        {credentials.map((credential) => (
          <CredentialItem
            key={credential.id}
            credential={credential}
            setSuccessMessage={setSuccessMessage}
            refreshData={refreshData}
          />
        ))}
      </Grid>
      {errorMessage && (
        <Toast type='error' onClose={clearError}>
          {errorMessage}
        </Toast>
      )}
      {successMessage && (
        <Toast type='success' onClose={clearSuccess}>
          {successMessage}
        </Toast>
      )}
      <AddCredentialDialog
        open={isDialogOpen}
        officialId={officialId}
        onClose={() => setIsDialogOpen(false)}
        onError={onError}
        onSubmit={onDialogSave}
      />
    </Box>
  );
};

export default CredentialsList;
